// src/pages/Auth/components/CreateNewPassword.js
import React from "react";
import {
  Grid,
  Typography,
  Paper,
  InputAdornment,
  Button,
  TextField,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import AuthContext from "../../../contexts/AuthContext";
import { completeNewPassword } from "../../../utilities/authFlow";
// ← you’ll need to add this helper in authFlow.js to call Cognito’s completeNewPassword API.

import "./AuthStyle.css";
import logo from "../../../assets/tnfmlogo.png";

const CreateNewPassword = ({ setPage }) => {
  const theme = useTheme();
  const { email, setEmail, tempSession, setTempSession, setSignedIn } =
    React.useContext(AuthContext);
  // ————————
  // “tempSession” is assumed to be the Cognito session object you received
  // when you first called `Auth.signIn(email, tempPassword)` and saw the
  // “NEW_PASSWORD_REQUIRED” challenge. You must store that session in context
  // so you can call the next step here. If you haven’t wired that up yet,
  // replace `tempSession` with whatever data Cognito needs to complete the challenge.
  // ————————
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [passwordVisibility, setPasswordVisibility] = React.useState(false);
  const [passwordError, setPasswordError] = React.useState("");
  const [confirmError, setConfirmError] = React.useState("");
  const [apiError, setApiError] = React.useState("");

  // Simple client-side check: nonempty + match
  const validate = () => {
    let ok = true;
    if (!newPassword || newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters");
      ok = false;
    } else {
      setPasswordError("");
    }
    if (newPassword !== confirmPassword) {
      setConfirmError("Passwords do not match");
      ok = false;
    } else {
      setConfirmError("");
    }
    return ok;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      // This helper should wrap Cognito’s completeNewPassword call.
      // e.g. AWS Amplify’s Auth.completeNewPassword(user, newPassword)
      // For raw aws‐sdk-cognito-js, it’s CognitoUser.completeNewPasswordChallenge(…).
      const result = await completeNewPassword(email, newPassword, tempSession);
      if (result.success) {
        // Clear the tempSession so they don’t see this page again.
        setTempSession(null);
        setSignedIn(true);
      } else {
        setApiError(result.error || "Unable to set new password");
      }
    } catch (e) {
      setApiError(e.message || "Error completing new password");
    }
  };

  return (
    <Grid
      container
      spacing={3}
      justifyContent="center"
      style={{
        height: "100%",
        backgroundColor: "#cfc9bc",
        fontFamily: "Instrument Sans, sans-serif",
      }}
    >
      <Grid
        item
        xs={12}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <img src={logo} height="150px" alt="TNFM Logo" />
        <Typography variant="h6">
          The Next Fun <span style={{ color: "#e6ff02" }}>Match</span>
        </Typography>
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
        lg={5}
        style={{ display: "flex", justifyContent: "center" }}
      >
        <Paper
          elevation={3}
          style={{ width: "100%", padding: 10, backgroundColor: "#11190c" }}
        >
          <Grid container direction="column" spacing={3}>
            <Grid item style={{ marginTop: 20, marginBottom: 20 }}>
              <Typography
                variant="h5"
                align="center"
                style={{ color: "#ffffff" }}
              >
                Create Your New Password
              </Typography>
            </Grid>

            {apiError ? (
              <Grid item style={{ display: "flex", justifyContent: "center" }}>
                <Typography
                  variant="subtitle2"
                  style={{
                    color: "#e6ff02",
                    paddingLeft: 20,
                  }}
                >
                  {apiError}
                </Typography>
              </Grid>
            ) : null}

            {/* Display email/username (read‐only) */}
            <Grid item style={{ display: "flex", justifyContent: "center" }}>
              <div style={{ width: "90%" }}>
                <label
                  htmlFor="email-display"
                  style={{
                    display: "block",
                    marginBottom: 12,
                    color: "#fff",
                    fontSize: "0.9rem",
                  }}
                >
                  Email / Username
                </label>
                <input
                  id="email-display"
                  type="text"
                  value={email}
                  readOnly
                  className="authInput"
                  style={{
                    backgroundColor: "#252b20",
                    borderRadius: "30px",
                    border: "1px solid #444",
                    color: "#fff",
                    width: "100%",
                    padding: "10px 16px",
                    boxSizing: "border-box",
                  }}
                />
              </div>
            </Grid>

            {/* New Password */}
            <Grid item style={{ display: "flex", justifyContent: "center" }}>
              <div style={{ width: "90%", position: "relative" }}>
                <label
                  htmlFor="new-password-input"
                  style={{
                    display: "block",
                    marginBottom: 12,
                    color: "#fff",
                    fontSize: "0.9rem",
                  }}
                >
                  New Password
                </label>
                <input
                  id="new-password-input"
                  type={passwordVisibility ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter a new password"
                  className="authInput"
                  style={{
                    paddingRight: "40px",
                  }}
                  autoComplete="off"
                />
                <div
                  onClick={() => setPasswordVisibility((vis) => !vis)}
                  style={{
                    position: "absolute",
                    right: 16,
                    top: 52,
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    color: "#fff",
                  }}
                >
                  {passwordVisibility ? <VisibilityOff /> : <Visibility />}
                </div>
                {passwordError && (
                  <Typography
                    variant="caption"
                    style={{ color: "#e6ff02", marginTop: 4, display: "block" }}
                  >
                    {passwordError}
                  </Typography>
                )}
              </div>
            </Grid>

            {/* Confirm Password */}
            <Grid item style={{ display: "flex", justifyContent: "center" }}>
              <div style={{ width: "90%", position: "relative" }}>
                <label
                  htmlFor="confirm-password-input"
                  style={{
                    display: "block",
                    marginBottom: 12,
                    color: "#fff",
                    fontSize: "0.9rem",
                  }}
                >
                  Confirm Password
                </label>
                <input
                  id="confirm-password-input"
                  type={passwordVisibility ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your new password"
                  className="authInput"
                  style={{
                    paddingRight: "40px",
                  }}
                  autoComplete="off"
                />
                <div
                  onClick={() => setPasswordVisibility((vis) => !vis)}
                  style={{
                    position: "absolute",
                    right: 16,
                    top: 52,
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    color: "#fff",
                  }}
                >
                  {passwordVisibility ? <VisibilityOff /> : <Visibility />}
                </div>
                {confirmError && (
                  <Typography
                    variant="caption"
                    style={{ color: "#e6ff02", marginTop: 4, display: "block" }}
                  >
                    {confirmError}
                  </Typography>
                )}
              </div>
            </Grid>

            {/* Submit Button */}
            <Grid item style={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                style={{
                  width: "90%",
                  height: 40,
                  backgroundColor: "#e6ff02",
                  color: "#252b20",
                }}
                onClick={handleSubmit}
              >
                Save New Password
              </Button>
            </Grid>

            <Grid item style={{ display: "flex", justifyContent: "center" }}>
              <Button
                style={{
                  width: "90%",
                  height: 40,
                  color: "#e6ff02",
                }}
                onClick={() => setPage("login")}
              >
                Back to Login
              </Button>
            </Grid>

            <Grid item style={{ marginTop: 0, marginBottom: 0 }}>
              <Typography
                variant="subtitle2"
                align="center"
                style={{
                  paddingLeft: 10,
                  paddingRight: 10,
                  color: "white",
                }}
              >
                Don't have an account? Click{" "}
                <span
                  onClick={() =>
                    window.open("https://thenextfunmatch.com", "_blank")
                  }
                  style={{
                    color: "#e6ff02",
                    cursor: "pointer",
                  }}
                >
                  here
                </span>{" "}
                to get in touch.
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Typography align="center" style={{ color: "#353535" }}>
              &copy; Aisle 2 LLC All Rights Reserved
            </Typography>
          </Grid>
          <Grid item>
            <Typography
              align="center"
              style={{ color: "#353535", marginBottom: 50, cursor: "pointer" }}
            >
              Privacy Policy
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default CreateNewPassword;
